import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { toast } from 'sonner';

const getBadgeVariant = (tag) => {
  switch (tag?.toLowerCase()) {
    case 'offers':
      return 'bg-foreground text-background';
    case 'more':
      return 'bg-muted text-muted-foreground';
    case 'store':
      return 'bg-foreground text-background';
    case 'featured':
      return 'bg-success text-success-foreground';
    case 'new':
      return 'bg-badge-new text-primary-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const ProductCard = ({ product, index, onProductClick }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isFavorited, toggleFavorite, isHeartAnimating } = useFavorites();
  const { id, name, price, rating, reviews, brandTag, imageUrl, brand, colors, availableSizes, category } = product;

  const isProductFavorited = isFavorited(id);
  const isAnimating = isHeartAnimating === id;

  const handleCardClick = (e) => {
    // Don't navigate if clicking on buttons
    if (e.target.closest('button')) return;
    if (onProductClick) {
      onProductClick(id);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    toggleFavorite({
      id,
      name,
      price,
      image: imageUrl,
      rating,
      reviews,
      brand: brand || 'StuffLab',
      brandTag,
      category,
      colors,
      availableSizes,
    });
    toast.success(isProductFavorited ? 'Removed from favorites' : 'Added to favorites');
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart({
      id,
      name,
      price,
      image: imageUrl,
      selectedSize: null,
      selectedColor: colors && colors.length > 0 ? colors[0] : null,
      quantity: 1,
      brand: brand || 'StuffLab',
      rating,
      reviews,
    });
    toast.success('Added to cart!', {
      description: `${name} has been added to your cart.`
    });
  };

  const handleBuyNow = (e) => {
    e.stopPropagation();
    addToCart({
      id,
      name,
      price,
      image: imageUrl,
      selectedSize: null,
      selectedColor: colors && colors.length > 0 ? colors[0] : null,
      quantity: 1,
      brand: brand || 'StuffLab',
      rating,
      reviews,
    });
    navigate('/cart');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        ease: 'easeOut'
      }}
      layout
      layoutId={`product-${id}`}
      onClick={handleCardClick}
    >
      <Card className="group relative bg-card border-0 rounded-[24px] overflow-hidden shadow-product transition-all duration-300 hover:shadow-product-hover hover:-translate-y-2 cursor-pointer">
        {/* Badge */}
        {brandTag && (
          <div className="absolute top-4 left-4 z-10">
            <Badge className={`${getBadgeVariant(brandTag)} rounded-full px-3 py-1 text-xs font-medium`}>
              {brandTag}
            </Badge>
          </div>
        )}

        {/* Favorite Heart Button */}
        <motion.button
          onClick={handleToggleFavorite}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={isAnimating ? { scale: [1, 1.3, 1] } : {}}
          transition={{ duration: 0.3 }}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-background transition-colors"
        >
          <Heart 
            className={`w-4 h-4 transition-colors ${
              isProductFavorited 
                ? 'fill-destructive text-destructive' 
                : 'text-muted-foreground hover:text-destructive'
            }`} 
          />
        </motion.button>

        {/* Product Image */}
        <CardContent className="p-6 pb-4">
          <div className="relative aspect-square bg-muted/30 rounded-2xl overflow-hidden mb-4">
            <motion.img
              src={imageUrl}
              alt={name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>

          {/* Product Info */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-foreground text-base leading-tight line-clamp-2">
                {name}
              </h3>
              <span className="font-bold text-foreground text-lg whitespace-nowrap">
                ${price.toFixed(2)}
              </span>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 fill-warning text-warning" />
              <span className="text-sm font-medium text-foreground">{rating}</span>
              <span className="text-sm text-muted-foreground">({reviews} Reviews)</span>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-2">
              <motion.div whileTap={{ scale: 0.95 }} className="flex-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full border-border hover:bg-muted transition-colors"
                  onClick={handleAddToCart}
                >
                  Add to Cart
                </Button>
              </motion.div>
              <motion.div whileTap={{ scale: 0.9 }} className="flex-1">
                <Button
                  size="sm"
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  onClick={handleBuyNow}
                >
                  Buy Now
                </Button>
              </motion.div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ProductCard;
